/*
 * Cross-sell "incentive" drawer (snippets/cross-sell-drawer.liquid, sections/cross-sell-drawer.liquid)
 *
 * Opened by ProductForm (theme.js) after an add to cart from a product page or a quick
 * add: the content is rendered for the added product through the Section Rendering API.
 * Without cross-sell, the cart drawer opens directly.
 * Adds from this drawer update the cart without opening another drawer; the progress
 * bar of the drawer is then refreshed.
 */
/*
 * Adds the variant of a cross-sell item form ([data-cross-sell-item], snippets/cross-sell-item.liquid)
 * to the cart and publishes the cart update, without opening any drawer.
 * Shared by the cross-sell drawer and the cart recommendations (assets/cart-recommendations.js).
 * Returns true once added.
 */
async function addCrossSellItem(form, errorMessage = "") {
  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector("[data-cross-sell-error]");
  if (button.hasAttribute("aria-busy")) return false;

  button.setAttribute("aria-busy", "true");
  error.hidden = true;

  try {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:bundled-sections", {
        bubbles: true,
        detail: { sections },
      }),
    );

    const response = await fetch(theme.routes.cart_add_url, {
      ...theme.utils.fetchConfig("javascript"),
      body: JSON.stringify({
        items: [{ id: Number(new FormData(form).get("id")), quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const added = await response.json();
    if (!response.ok || added.status) throw added;

    const cart = await (
      await fetch(theme.routes.cart_url, {
        ...theme.utils.fetchConfig("json", "GET"),
      })
    ).json();
    cart.sections = added.sections;
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source: "cross-sell",
      cart,
    });

    // The button shows a check instead of the cart icon
    form.classList.add("is-added");
    button.setAttribute("aria-label", button.dataset.addedLabel);
    // The cart update moves the focus to the cart drawer
    if (button.isConnected) button.focus({ preventScroll: true });
    return true;
  } catch (failure) {
    console.error(failure);
    error.textContent = failure?.description || errorMessage;
    error.hidden = false;
    return false;
  } finally {
    button.removeAttribute("aria-busy");
  }
}

/*
 * Updates the displayed prices of a cross-sell item from its selected variant
 * (data attributes of the options, snippets/cross-sell-item.liquid).
 */
function updateCrossSellItemPrice(select) {
  const option = select.selectedOptions[0];
  const item = select.closest("[data-cross-sell-item]");
  const compare = item.querySelector("[data-cross-sell-compare]");
  const percent = item.querySelector("[data-cross-sell-percent]");
  item.querySelector("[data-cross-sell-price]").textContent = option.dataset.price;
  compare.textContent = option.dataset.compare || "";
  percent.textContent = option.dataset.percent || "";
  compare.hidden = percent.hidden = !option.dataset.compare;
}

if (!customElements.get("cross-sell-drawer")) {
  customElements.define(
    "cross-sell-drawer",
    class CrossSellDrawer extends DrawerElement {
      constructor() {
        super();

        // Listeners on the element itself (the drawer is moved to <body> when opened)
        this.addEventListener("submit", this.onSubmit.bind(this));
        this.addEventListener("change", this.onVariantChange.bind(this));
        this.addEventListener("click", this.onViewCartClick.bind(this));
      }

      get cartDrawer() {
        return document.querySelector("cart-drawer");
      }

      get content() {
        return this.querySelector("[data-cross-sell-drawer-content]");
      }

      sectionUrl(item) {
        const root = theme.routes.root_url.replace(/\/$/, "");
        return `${root}/products/${item.handle}?section_id=cross-sell-drawer&variant=${item.variant_id}`;
      }

      async fetchContent(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Cross-sell section: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), "text/html");
        return html.querySelector("[data-cross-sell-content]");
      }

      // Called by ProductForm with the line item returned by /cart/add
      async showFor(item, activeElement = null) {
        try {
          if (!item?.handle) throw new Error("Cross-sell: no product handle");

          this.url = this.sectionUrl(item);
          const content = await this.fetchContent(this.url);
          // TEMP DEBUG: open this URL and look for the "cross-sell-debug" comment
          console.info("[cross-sell]", this.url, "items:", content?.dataset.count);
          if (!content?.querySelector("[data-cross-sell-item]")) {
            this.cartDrawer?.show(activeElement);
            return;
          }

          this.content.innerHTML = content.outerHTML;
          this.show(activeElement);
        } catch (error) {
          console.error(error);
          this.cartDrawer?.show(activeElement);
        }
      }

      onViewCartClick(event) {
        const link = event.target.closest("[data-cross-sell-view-cart]");
        if (!link) return;

        event.preventDefault();
        this.hide();
        this.cartDrawer?.show();
      }

      // The variant picker updates the displayed price
      onVariantChange(event) {
        const select = event.target.closest("[data-cross-sell-variant]");
        if (select) updateCrossSellItemPrice(select);
      }

      async onSubmit(event) {
        const form = event.target.closest("[data-cross-sell-item]");
        if (!form) return;
        event.preventDefault();

        if (await addCrossSellItem(form, this.dataset.errorMessage)) {
          this.refreshProgress();
        }
      }

      async refreshProgress() {
        if (!this.url) return;
        try {
          const content = await this.fetchContent(this.url);
          const progress = this.querySelector("[data-cross-sell-progress]");
          const freshProgress = content?.querySelector("[data-cross-sell-progress]");
          if (progress && freshProgress) progress.innerHTML = freshProgress.innerHTML;
        } catch (error) {
          console.error(error);
        }
      }
    },
  );
}
